/**
 * Global Command Palette — ⌘K / Ctrl+K to jump to any page.
 *
 * Fuzzy search across ~50 routes. Shows a "Recent" section from localStorage
 * so users' most-visited pages are 1 keystroke away.
 *
 * Mobile: opens as a full-screen sheet, thumb-friendly, tap-to-navigate.
 */
import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/components/ui/command";
import {
  Home, ShoppingBag, Users, KeyRound, Inbox, Send, Smartphone, MessageCircle,
  Heart, ArrowLeftRight, Compass, TrendingUp, Bell, Sparkles, Wallet,
  Radar, Zap, Video, Palette, PenTool, Calendar, Layers, FolderTree,
  Package, RotateCcw, DollarSign, LifeBuoy, ScrollText, ShieldAlert, Gavel,
  Award, Store, Megaphone, Link2, FileText, BarChart3, ImageIcon, Trophy,
  Vote, ClipboardList, User, Settings, LogOut, Search, Rocket, Boxes,
  ShoppingCart,
} from "lucide-react";

type Item = { title: string; group: string; path: string; icon: any; keywords?: string };

const ITEMS: Item[] = [
  // Core
  { group: "Core",         title: "Dashboard",              path: "/dashboard",                 icon: Home,          keywords: "home main overview" },
  { group: "Core",         title: "Shop",                   path: "/shop",                      icon: ShoppingBag,   keywords: "products browse marketplace" },
  { group: "Core",         title: "Wishlist",               path: "/wishlist",                  icon: Heart },
  { group: "Core",         title: "Compare",                path: "/compare",                   icon: ArrowLeftRight },
  { group: "Core",         title: "Discovery",              path: "/discovery",                 icon: Compass },
  { group: "Core",         title: "Trends",                 path: "/trends",                    icon: TrendingUp },

  // Signal → Action
  { group: "AI Studio",    title: "Signal → Action",        path: "/workspace/ai/signal-to-action", icon: Zap,        keywords: "sae comments leads" },
  { group: "AI Studio",    title: "Intent Radar",           path: "/workspace/intent-radar",    icon: Radar,         keywords: "buyers prediction sellers who will buy contact" },
  { group: "AI Studio",    title: "Viral Waves",            path: "/workspace/ai/viral-waves",  icon: Radar,         keywords: "detector predict" },
  { group: "AI Studio",    title: "AutoPilot",              path: "/workspace/ai/autopilot",    icon: Sparkles },
  { group: "AI Studio",    title: "Brand Studio",           path: "/workspace/ai/brand-studio", icon: Palette,       keywords: "banana image ai" },
  { group: "AI Studio",    title: "AI Blog",                path: "/workspace/ai/blog",         icon: PenTool },
  { group: "AI Studio",    title: "Money Meter",            path: "/workspace/ai/money-meter",  icon: Wallet },
  { group: "AI Studio",    title: "AI Cart™",                path: "/ai-cart",                   icon: ShoppingCart,  keywords: "cart compare amazon flipkart url paste" },

  // Create
  { group: "Create",       title: "Composer",               path: "/composer",                  icon: PenTool,       keywords: "new post write" },
  { group: "Create",       title: "Calendar",               path: "/calendar",                  icon: Calendar,      keywords: "schedule" },
  { group: "Create",       title: "Posts",                  path: "/posts",                     icon: Layers },
  { group: "Create",       title: "Content Library",        path: "/library",                   icon: FolderTree,    keywords: "media assets" },

  // Sell
  { group: "Sell",         title: "Orders",                 path: "/workspace/sell/orders",     icon: ClipboardList, keywords: "order sold shipped" },
  { group: "Sell",         title: "Inventory",              path: "/workspace/sell/inventory",  icon: Package,       keywords: "stock" },
  { group: "Sell",         title: "Returns",                path: "/workspace/sell/returns",    icon: RotateCcw },
  { group: "Sell",         title: "Payouts",                path: "/workspace/sell/payouts",    icon: DollarSign,    keywords: "money earnings" },
  { group: "Sell",         title: "Credit Ledger",          path: "/workspace/credits",         icon: Wallet },

  // Catalog
  { group: "Catalog",      title: "Product Wizard",         path: "/workspace/catalog/manual",  icon: Boxes,         keywords: "add product new item" },
  { group: "Catalog",      title: "Bulk CSV Import",        path: "/workspace/catalog/bulk",    icon: Boxes,         keywords: "upload csv import" },
  { group: "Catalog",      title: "AI Copy",                path: "/workspace/catalog/ai-copy", icon: Sparkles },
  { group: "Catalog",      title: "Media Library",          path: "/workspace/catalog/media",   icon: ImageIcon },
  { group: "Catalog",      title: "Collections",            path: "/workspace/catalog/collections", icon: Layers },
  { group: "Catalog",      title: "Store Sync",             path: "/workspace/catalog/stores",  icon: Store,         keywords: "woocommerce shopify amazon" },

  // Marketing
  { group: "Marketing",    title: "SMS Composer",           path: "/workspace/marketing/sms",       icon: Smartphone, keywords: "twilio text message" },
  { group: "Marketing",    title: "WhatsApp Composer",      path: "/workspace/marketing/whatsapp",  icon: MessageCircle, keywords: "twilio wa chat" },
  { group: "Marketing",    title: "Broadcast",              path: "/workspace/marketing/broadcast", icon: Megaphone,  keywords: "bulk campaign blast" },
  { group: "Marketing",    title: "Ads",                    path: "/workspace/marketing/ads",       icon: TrendingUp },
  { group: "Marketing",    title: "URL Shortener",          path: "/workspace/marketing/shortener", icon: Link2 },
  { group: "Marketing",    title: "Launch Studio",          path: "/workspace/marketing/launch",    icon: Rocket },

  // Brand
  { group: "Brand",        title: "Reputation",             path: "/workspace/brand/reputation",  icon: ShieldAlert },
  { group: "Brand",        title: "Sentiment",              path: "/workspace/brand/sentiment",   icon: Sparkles },
  { group: "Brand",        title: "Competitors",            path: "/workspace/brand/competitors", icon: Users },
  { group: "Brand",        title: "History",                path: "/workspace/brand/history",     icon: ScrollText },
  { group: "Brand",        title: "Campaigns",              path: "/workspace/brand/campaigns",   icon: Megaphone },
  { group: "Brand",        title: "Insights",               path: "/workspace/brand/insights",    icon: BarChart3 },

  // Creator
  { group: "Creator",      title: "Audience",               path: "/workspace/creator/audience",    icon: Users },
  { group: "Creator",      title: "Performance",            path: "/workspace/creator/performance", icon: BarChart3 },
  { group: "Creator",      title: "Collabs",                path: "/workspace/creator/collabs",     icon: Users },
  { group: "Creator",      title: "Topics",                 path: "/workspace/creator/topics",      icon: FolderTree },

  // Measure
  { group: "Measure",      title: "Analytics",              path: "/analytics",                 icon: BarChart3 },
  { group: "Measure",      title: "Insights",               path: "/insights",                  icon: TrendingUp },
  { group: "Measure",      title: "Platforms",              path: "/platforms",                 icon: Video },

  // Grow
  { group: "Grow",         title: "Referrals",              path: "/referrals",                 icon: Users,         keywords: "invite friends earn" },
  { group: "Grow",         title: "Credits & Billing",      path: "/settings/billing",          icon: Wallet },
  { group: "Grow",         title: "Leads",                  path: "/leads",                     icon: Users },

  // Support
  { group: "Support",      title: "Help Center",            path: "/workspace/support/help",    icon: LifeBuoy },
  { group: "Support",      title: "SIBA Score Public",      path: "/score",                     icon: Award },
  { group: "Support",      title: "Retry Center",           path: "/retry",                     icon: RotateCcw },

  // Super Admin
  { group: "Super Admin",  title: "SMS / WhatsApp Inbox",   path: "/superadmin/inbox",          icon: Inbox,         keywords: "twilio replies two way messages" },
  { group: "Super Admin",  title: "Ticket Analytics",       path: "/superadmin/support",        icon: ScrollText,    keywords: "tickets sla" },
  { group: "Super Admin",  title: "Reviews Moderation",     path: "/superadmin/moderation",     icon: ShieldAlert },
  { group: "Super Admin",  title: "Ban Appeals",            path: "/superadmin/appeals",        icon: Gavel },
  { group: "Super Admin",  title: "Super Admin Dashboard",  path: "/superadmin",                icon: Trophy },

  // Admin
  { group: "Admin",        title: "Admin Setup",            path: "/admin/setup",               icon: KeyRound,      keywords: "config oauth payments twilio marketing notifications" },
  { group: "Admin",        title: "Notifications Config",   path: "/admin/setup?tab=notifications", icon: Bell,       keywords: "channels toggle email sms whatsapp templates" },
  { group: "Admin",        title: "Marketing Providers",    path: "/admin/setup?tab=payments",  icon: KeyRound,      keywords: "twilio api keys" },
  { group: "Admin",        title: "Payments Config",        path: "/admin/setup?tab=payments",  icon: DollarSign,    keywords: "stripe razorpay" },
  { group: "Admin",        title: "User Management",        path: "/admin/users",               icon: Users },
  { group: "Admin",        title: "Subscriptions",          path: "/admin/subscriptions",       icon: Wallet },
  { group: "Admin",        title: "Testimonials Wall",      path: "/admin/testimonials",        icon: MessageCircle },
  { group: "Admin",        title: "Referral Payouts",       path: "/admin/payouts",             icon: DollarSign },
  { group: "Admin",        title: "UTM Report",             path: "/admin/utm-report",          icon: BarChart3 },

  // Public
  { group: "Public",       title: "Vote (Contest)",         path: "/vote",                      icon: Vote },
  { group: "Public",       title: "Wins (Recap)",           path: "/wins",                      icon: Trophy },
  { group: "Public",       title: "Pricing",                path: "/pricing",                   icon: DollarSign },
  { group: "Public",       title: "Landing",                path: "/",                          icon: Home },

  // Account
  { group: "Account",      title: "Profile",                path: "/profile",                   icon: User },
  { group: "Account",      title: "Settings",               path: "/settings",                  icon: Settings },
];

const RECENT_KEY = "sb_cmdk_recent";
const MAX_RECENT = 5;


function loadRecent(): string[] {
  try {
    return JSON.parse(localStorage.getItem(RECENT_KEY) || "[]").filter((p: any) => typeof p === "string").slice(0, MAX_RECENT);
  } catch { return []; }
}

function pushRecent(path: string): void {
  try {
    const cur = loadRecent().filter((p) => p !== path);
    cur.unshift(path);
    localStorage.setItem(RECENT_KEY, JSON.stringify(cur.slice(0, MAX_RECENT)));
  } catch { /* ignore */ }
}


interface Props { open: boolean; onOpenChange: (open: boolean) => void; }

export const CommandPalette: React.FC<Props> = ({ open, onOpenChange }) => {
  const navigate = useNavigate();
  const [recent, setRecent] = React.useState<string[]>(loadRecent());

  React.useEffect(() => { if (open) setRecent(loadRecent()); }, [open]);

  const go = (path: string) => {
    pushRecent(path);
    onOpenChange(false);
    // brief delay so the dialog animates out cleanly
    setTimeout(() => navigate(path), 60);
  };

  const recentItems = recent
    .map((p) => ITEMS.find((it) => it.path === p))
    .filter(Boolean) as Item[];

  // Group items by their 'group' key while preserving declared order.
  const grouped = React.useMemo(() => {
    const g: Record<string, Item[]> = {};
    for (const it of ITEMS) {
      (g[it.group] = g[it.group] || []).push(it);
    }
    return g;
  }, []);

  return (
    <CommandDialog open={open} onOpenChange={onOpenChange}>
      <CommandInput placeholder="Jump to any page… (try 'inbox', 'twilio', 'orders')" data-testid="cmdk-input"/>
      <CommandList data-testid="cmdk-list" className="max-h-[70vh]">
        <CommandEmpty>No matching page. Try shorter keywords.</CommandEmpty>

        {recentItems.length > 0 && (
          <>
            <CommandGroup heading="Recent">
              {recentItems.map((it) => (
                <CommandItem key={`recent-${it.path}`} value={`recent ${it.title} ${it.keywords || ""}`} onSelect={() => go(it.path)} data-testid={`cmdk-recent-${it.path.replace(/\//g, "-")}`}>
                  <it.icon className="h-4 w-4 mr-2 opacity-70"/>
                  <span>{it.title}</span>
                  <span className="ml-auto text-[10px] text-[color:var(--sg-muted)] opacity-70">{it.group}</span>
                </CommandItem>
              ))}
            </CommandGroup>
            <CommandSeparator/>
          </>
        )}

        {Object.entries(grouped).map(([group, items]) => (
          <CommandGroup key={group} heading={group}>
            {items.map((it) => (
              <CommandItem
                key={it.path}
                value={`${it.title} ${it.keywords || ""} ${it.group}`}
                onSelect={() => go(it.path)}
                data-testid={`cmdk-item-${it.path.replace(/\//g, "-").replace(/\?/g, "-")}`}
              >
                <it.icon className="h-4 w-4 mr-2 opacity-70"/>
                <span>{it.title}</span>
                <span className="ml-auto text-[10px] text-[color:var(--sg-muted)] opacity-70">{it.group}</span>
              </CommandItem>
            ))}
          </CommandGroup>
        ))}
      </CommandList>
    </CommandDialog>
  );
};


/** Provider — mounts the palette once and wires ⌘K / Ctrl+K + also exposes an
 * imperative open() via window event for the mobile top-bar search icon. */
export const CommandPaletteProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [open, setOpen] = React.useState(false);
  const location = useLocation();

  // Record every visited page so the Recent list is always fresh.
  React.useEffect(() => {
    const p = location.pathname + (location.search || "");
    // Only track known items (avoid junk like /r/:code)
    if (ITEMS.some((it) => it.path === p || it.path === location.pathname)) {
      pushRecent(location.pathname);
    }
  }, [location.pathname]);

  React.useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      // ⌘K on Mac, Ctrl+K on Win/Linux, or "/" as a secondary trigger
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpen((o) => !o);
        return;
      }
      if (e.key === "/" && !(e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement)) {
        e.preventDefault();
        setOpen(true);
      }
    };
    const onExternal = () => setOpen(true);
    window.addEventListener("keydown", onKey);
    window.addEventListener("sb:open-cmdk", onExternal);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("sb:open-cmdk", onExternal);
    };
  }, []);

  return (
    <>
      {children}
      <CommandPalette open={open} onOpenChange={setOpen}/>
    </>
  );
};


/** Small pill/button any page can render to open the palette. */
export const CommandPaletteButton: React.FC<{ compact?: boolean }> = ({ compact }) => {
  const isMac = typeof navigator !== "undefined" && /Mac|iPhone|iPad/.test(navigator.platform);
  return (
    <button
      type="button"
      data-testid="cmdk-open-btn"
      onClick={() => window.dispatchEvent(new Event("sb:open-cmdk"))}
      className={`inline-flex items-center gap-2 rounded-full border border-[color:var(--sg-border)] bg-[color:var(--sg-muted-bg)] hover:bg-[color:var(--sg-hover-bg,rgba(0,0,0,0.06))] text-[color:var(--sg-muted)] transition ${compact ? "h-8 w-8 justify-center" : "px-3 h-8"}`}
      aria-label="Open command palette (Ctrl+K)"
    >
      <Search className="h-3.5 w-3.5"/>
      {!compact && (
        <>
          <span className="text-xs">Jump to…</span>
          <kbd className="hidden sm:inline text-[10px] font-mono bg-[color:var(--sg-card)] border border-[color:var(--sg-border)] rounded px-1.5 py-0.5">{isMac ? "⌘K" : "Ctrl+K"}</kbd>
        </>
      )}
    </button>
  );
};
